import {
  Component,
  Input,
  Output,
  EventEmitter,
  SecurityContext,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EmailData, DEFAULT_EMAIL_DATA } from '../../services/email-preview.service';

/**
 * Renders a preview of an email, including sanitized HTML content, resolved logo and hero images, and contact/address information; provides an optional "View in browser" action.
 * @component
 * @selector app-email-preview
 * @example
 *   <app-email-preview [emailData]="sampleEmail" [showViewInBrowser]="true" (viewInBrowser)="onViewInBrowser()"></app-email-preview>
 *
 * Purpose:
 *   - Display an email preview using provided EmailData.
 *   - Protect against XSS by sanitizing any HTML content before rendering.
 *   - Resolve images and logos from strings, File objects, or fallback URLs.
 *   - Format and expose contact/address information for display.
 *
 * Key methods:
 *   - onViewInBrowser(): void - Emits the viewInBrowser output when the user requests to open the email in a browser view.
 *   - sanitizeHtml(html: string | null | undefined): SafeHtml - Uses DomSanitizer to sanitize HTML content and returns a SafeHtml (or empty string for null/undefined).
 *   - getLogoUrl(): string - Returns the best available logo URL from emailData (string, File -> object URL, explicit logoUrl) or a default asset.
 *   - getImageUrl(): string - Returns the best available hero/image URL from emailData (string, File -> object URL, explicit imageUrl) or a default asset.
 *   - getFullAddress(): string - Concatenates address parts (name, street, city, state, zip) into a single, comma-separated address string.
 *   - getContactInfo(): SafeHtml - Builds contact info (address, email, phone) as HTML (line breaks) and sanitizes it for safe rendering.
 *
 * Lifecycle and dependencies:
 *   - constructor(private sanitizer: DomSanitizer) is used to inject Angular's DomSanitizer for HTML sanitization.
 *   - No additional Angular lifecycle hooks (ngOnInit, ngOnChanges, etc.) are required by this component implementation.
 *
 * @input EmailData emailData - The email payload to display (logo/image fields, optional URLs or File objects, address and contact fields, body HTML). Defaults to DEFAULT_EMAIL_DATA.
 * @input boolean showViewInBrowser - Toggle to show or hide the "View in browser" action (default: true).
 * @output EventEmitter<void> viewInBrowser - Emitted when the component's "View in browser" action is triggered by the user.
 */
@Component({
  selector: 'app-email-preview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './email-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./email-preview.component.scss'],
})
export class EmailPreviewComponent {
  @Input() emailData: EmailData = DEFAULT_EMAIL_DATA;

  @Output() viewInBrowser = new EventEmitter<void>();

  @Input() showViewInBrowser = true;

  constructor(private sanitizer: DomSanitizer) {}

  onViewInBrowser(): void {
    this.viewInBrowser.emit();
  }

  /**
   * Sanitize HTML content to prevent XSS attacks
   */
  sanitizeHtml(html: string | null | undefined): SafeHtml {
    if (!html) {
      return '';
    }
    return this.sanitizer.sanitize(SecurityContext.HTML, html) || '';
  }

  // Helper methods for image URLs
  /**
   * Resolve and return the URL to use for the email logo; prefers a string logo, then a File object (converted with createObjectURL), then logoUrl, and falls back to a default asset.
   * @example
   * etLogoUrl()
   * 'assets/images/Frame 1171276840.svg'
   * @returns {string} The resolved logo URL.
   **/
  getLogoUrl(): string {
    if (this.emailData.logo && typeof this.emailData.logo === 'string') {
      return this.emailData.logo;
    }
    if (this.emailData.logo && this.emailData.logo instanceof File) {
      return URL.createObjectURL(this.emailData.logo);
    }
    if (this.emailData.logoUrl) {
      return this.emailData.logoUrl;
    }
    return 'assets/images/Frame 1171276840.svg'; // Default logo
  }

  /**
   * Determine and return the image URL to display for the email preview.
   * @example
   * etImageUrl()
   * 'assets/images/Hero-Image-1.svg'
   * @returns {string} Resolved image URL (string) or default asset path.
   */
  getImageUrl(): string {
    if (this.emailData.image && typeof this.emailData.image === 'string') {
      return this.emailData.image;
    }
    if (this.emailData.image && this.emailData.image instanceof File) {
      return URL.createObjectURL(this.emailData.image);
    }
    if (this.emailData.imageUrl) {
      return this.emailData.imageUrl;
    }
    return 'assets/images/Hero-Image-1.svg'; // Default image
  }

  getFullAddress(): string {
    const parts = [
      this.emailData.carWashName || '',
      this.emailData.street || '',
      this.emailData.city || '',
      this.emailData.state || '',
      this.emailData.zipCode || '',
    ].filter(part => part.trim());
    return parts.join(', ');
  }

  getContactInfo(): SafeHtml {
    const parts = [
      this.getFullAddress(),
      this.emailData.email || '',
      this.emailData.phoneNumber || '',
    ].filter(part => part.trim());
    const html = parts.join('<br>');
    return this.sanitizeHtml(html);
  }
}
